import { Activity, useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ParameterSection from './ParameterSection'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { usePageTitle } from '../../context/PageTitleContext'

const GROUP_UNITS = ['file', 'recording', 'burst', 'observation', 'collection']

const EditAnalysisPage = () => {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const { setBreadcrumbs } = usePageTitle()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const [analysisData, setAnalysisData] = useState({
    name: '',
    description: '',
    input_group_unit: '',
    output_group_unit: '',
    input_datatype_ids: [],
    parameters: {},
    default_parameters: {},
    timeseries_parameters: {},
    histogram_parameters: {},
    gpu_required: false,
  })

  const [dataTypes, setDataTypes] = useState([])
  const [selectedDataTypes, setSelectedDataTypes] = useState([])

  const [errors, setErrors] = useState({})

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const [analysisResponse, dataTypesResponse] = await Promise.all([
          fetchWithAuth(`analyses/${uuid}`),
          fetchWithAuth('data-types'),
        ])

        const analysis = analysisResponse.data
        const dataTypesList = dataTypesResponse.data?.data || []

        setDataTypes(dataTypesList)

        const selectedTypes = []

        if (analysis.input_datatype_ids && dataTypesList.length > 0) {
          analysis.input_datatype_ids.forEach((dt) => {
            if (typeof dt === 'object') {
              if (dt.uuid) {
                const match = dataTypesList.find(
                  (item) => item.uuid === dt.uuid,
                )
                if (match) {
                  selectedTypes.push(match)
                  return
                }
              }

              if (dt.slug) {
                const match = dataTypesList.find(
                  (item) => item.slug === dt.slug,
                )
                if (match) {
                  selectedTypes.push(match)
                  return
                }
              }
            } else if (typeof dt === 'string') {
              const match = dataTypesList.find(
                (item) => item.uuid === dt || item._id === dt || item.id === dt,
              )
              if (match) {
                selectedTypes.push(match)
              }
            }
          })
        }

        setSelectedDataTypes(selectedTypes)

        setAnalysisData({
          name: analysis.name || '',
          description: analysis.description || '',
          input_group_unit: analysis.input_group_unit || '',
          output_group_unit: analysis.output_group_unit || '',
          input_datatype_ids: selectedTypes.map((dt) => dt.uuid),
          parameters: analysis.parameters || {},
          default_parameters: analysis.default_parameters || {},
          timeseries_parameters: analysis.timeseries_parameters || {},
          histogram_parameters: analysis.histogram_parameters || {},
          gpu_required: analysis.gpu_required || false,
        })
        setBreadcrumbs(['analyses', analysis.name, 'edit'])
      } catch (err) {
        console.error('Error fetching analysis data:', err)
        setError('Failed to load analysis. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    return () => setBreadcrumbs([])
  }, [uuid])

  const validate = () => {
    const newErrors = {}

    if (!analysisData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!analysisData.description.trim()) {
      newErrors.description = 'Description is required'
    }

    if (!analysisData.input_group_unit) {
      newErrors.input_group_unit = 'Input group unit is required'
    }

    if (!analysisData.output_group_unit) {
      newErrors.output_group_unit = 'Output group unit is required'
    }

    if (analysisData.input_datatype_ids.length === 0) {
      newErrors.input_datatype_ids = 'At least one input data type is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    setAnalysisData({
      ...analysisData,
      [e.target.name]: e.target.value,
    })

    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: undefined,
      })
    }
  }

  const handleDataTypesChange = (_event, newValue) => {
    setSelectedDataTypes(newValue)
    setAnalysisData({
      ...analysisData,
      input_datatype_ids: newValue.map((dt) => dt.uuid),
    })

    if (errors.input_datatype_ids) {
      setErrors({
        ...errors,
        input_datatype_ids: undefined,
      })
    }
  }

  const handleGpuChange = (event) => {
    setAnalysisData({
      ...analysisData,
      gpu_required: event.target.checked,
    })
  }

  const handleAddParameter = (field) => (key, value) => {
    setAnalysisData((prev) => ({
      ...prev,
      [field]: { ...prev[field], [key]: value },
    }))
  }

  const handleRemoveParameter = (field) => (key) => {
    setAnalysisData((prev) => {
      const updated = { ...prev[field] }
      delete updated[key]
      return { ...prev, [field]: updated }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    try {
      setSaving(true)
      setError(null)
      setSuccess(false)

      const response = await fetchWithAuth(`analyses/${uuid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(analysisData),
      })

      if (response.status === 200) {
        setTimeout(() => {
          navigate(`/analyses/${uuid}`)
        }, 300)
        setSuccess(true)
      } else {
        const data = await response.json()
        if (data.errors) {
          const apiErrors = {}
          data.errors.forEach((err) => {
            apiErrors[err.field] = err.message
          })
          setErrors(apiErrors)
        } else {
          setError(data.message || 'Failed to update analysis')
        }
      }
    } catch (err) {
      console.error('Error updating analysis:', err)
      setError('Failed to update analysis. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading analysis data...
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton component={Link} to={`/analyses/${uuid}`} edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          Edit Analysis
        </Typography>
      </Stack>

      <Activity mode={error ? 'visible' : 'hidden'}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Activity>

      <Activity mode={success ? 'visible' : 'hidden'}>
        <Alert severity="success" sx={{ mb: 3 }}>
          Analysis updated successfully! Redirecting...
        </Alert>
      </Activity>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Name"
              name="name"
              value={analysisData.name}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name}
            />

            <TextField
              label="Description"
              name="description"
              value={analysisData.description}
              onChange={handleChange}
              fullWidth
              required
              multiline
              rows={4}
              error={!!errors.description}
              helperText={errors.description}
            />

            <FormControlLabel
              control={
                <Switch
                  name="gpu_required"
                  checked={analysisData.gpu_required}
                  onChange={handleGpuChange}
                  slotProps={{ input: { 'aria-label': 'Needs GPU' } }}
                />
              }
              label="Needs Gpu"
            />

            <FormControl fullWidth error={!!errors.input_group_unit} required>
              <InputLabel
                id="input-group-unit-label"
                htmlFor="input-group-unit"
              >
                Input Group Unit
              </InputLabel>
              <Select
                labelId="input-group-unit-label"
                label="Input Group Unit"
                inputProps={{ id: 'input-group-unit' }}
                name="input_group_unit"
                value={analysisData.input_group_unit}
                onChange={handleChange}
              >
                {GROUP_UNITS.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
              <Activity mode={errors.input_group_unit ? 'visible' : 'hidden'}>
                <FormHelperText>{errors.input_group_unit}</FormHelperText>
              </Activity>
            </FormControl>

            <FormControl fullWidth error={!!errors.output_group_unit} required>
              <InputLabel
                id="output-group-unit-label"
                htmlFor="output-group-unit"
              >
                Output Group Unit
              </InputLabel>
              <Select
                labelId="output-group-unit-label"
                label="Output Group Unit"
                inputProps={{ id: 'output-group-unit' }}
                name="output_group_unit"
                value={analysisData.output_group_unit}
                onChange={handleChange}
              >
                {GROUP_UNITS.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
              <Activity mode={errors.output_group_unit ? 'visible' : 'hidden'}>
                <FormHelperText>{errors.output_group_unit}</FormHelperText>
              </Activity>
            </FormControl>

            <Autocomplete
              multiple
              id="input_datatype_ids"
              options={dataTypes}
              value={selectedDataTypes}
              getOptionLabel={(option) => option.name}
              onChange={handleDataTypesChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Input Data Types"
                  error={!!errors.input_datatype_ids}
                  helperText={errors.input_datatype_ids}
                />
              )}
            />

            <ParameterSection
              title="Parameters"
              parameters={analysisData.parameters}
              keyLabel="Parameter Key"
              valueLabel="Parameter Value"
              onAdd={handleAddParameter('parameters')}
              onRemove={handleRemoveParameter('parameters')}
            />

            <Divider sx={{ my: 2 }} />

            <ParameterSection
              title="Default Parameters"
              parameters={analysisData.default_parameters}
              keyLabel="Default Parameter Key"
              valueLabel="Default Parameter Value"
              onAdd={handleAddParameter('default_parameters')}
              onRemove={handleRemoveParameter('default_parameters')}
            />

            <Divider sx={{ my: 2 }} />

            <ParameterSection
              title="Timeseries Parameters"
              parameters={analysisData.timeseries_parameters}
              keyLabel="Timeseries Parameter"
              valueLabel="Timeseries Parameter Label"
              onAdd={handleAddParameter('timeseries_parameters')}
              onRemove={handleRemoveParameter('timeseries_parameters')}
            />

            <Divider sx={{ my: 2 }} />

            <ParameterSection
              title="Histogram Parameters"
              parameters={analysisData.histogram_parameters}
              keyLabel="Histogram Parameter"
              valueLabel="Histogram Parameter Label"
              onAdd={handleAddParameter('histogram_parameters')}
              onRemove={handleRemoveParameter('histogram_parameters')}
            />

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                size="large"
                disabled={saving}
                startIcon={
                  saving ? <CircularProgress size={20} color="inherit" /> : null
                }
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to={`/analyses/${uuid}`}
                disabled={saving}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  )
}

export default EditAnalysisPage
